/* istanbul ignore file */
import { type ContentType } from '@lumapps/content-types/types';
import { designerContentCreation, designerContentEdition } from '@lumapps/designer-content-editor/routes';
import { Route } from '@lumapps/router';
import { AppId } from '@lumapps/router/constants';

import { ANCHOR_FROM_FO } from '../constants';
import type { LegacyContentStatus } from '../types';

const contentEditPrefix = '/content/edit/';
const contentCreatePrefix = '/content/create/';
const contentCopyPrefix = '/content/duplicate/';

const contentEditRoute: Route = {
    // The param names should be the same as in the legacy state.js $stateParams
    path: `${contentEditPrefix}:key`,
    params: {
        contentType: 'content',
        // The content id.
        key: undefined,
        // Optional space context param (passed as query param).
        spaceId: undefined,
    },
    legacyId: 'app.front.content-edit',
    appId: AppId.legacy,
};

const contentCreateRoute: Route = {
    // The param names should be the same as in the legacy state.js $stateParams
    path: `${contentCreatePrefix}:type/:customContentType?/:template?`,
    params: {
        // The content type.
        type: undefined,
        // The custom content type ID.
        customContentType: undefined,
        // Optional template ID.
        template: undefined,
        // Optional space context param (passed as query param).
        spaceId: undefined,
    },
    legacyId: 'app.front.content-create',
    appId: AppId.legacy,
};

const contentCopyRoute = {
    // The param names should be the same as in the legacy state.js $stateParams
    path: `${contentCopyPrefix}:key`,
    params: {
        // The content id.
        key: undefined,
    },
    legacyId: 'app.front.content-duplicate',
    appId: AppId.legacy,
};

export const isDesignerUrl = (slug: string) =>
    slug.indexOf(contentEditPrefix) === 0 || slug.indexOf(contentCreatePrefix) === 0;

export const contentEdit = (
    contentId: string,
    {
        anchor,
        shouldOpenWriterEditor,
    }: {
        anchor?: typeof ANCHOR_FROM_FO;
        shouldOpenWriterEditor?: boolean;
    } = {},
): Route =>
    shouldOpenWriterEditor
        ? designerContentEdition(contentId)
        : {
              ...contentEditRoute,
              anchor,
              params: {
                  ...contentEditRoute.params,
                  key: contentId,
              },
          };

export const contentCreate = (type: ContentType, customContentType?: string, template?: string): Route => ({
    ...contentCreateRoute,
    params: {
        ...contentCreateRoute.params,
        type,
        customContentType,
        template,
    },
});

export const contentCopy = (contentId: string, status?: LegacyContentStatus): Route => ({
    ...contentCopyRoute,
    params: {
        ...contentCopyRoute.params,
        key: contentId,
        status,
    },
});

/**
 * Navigate to content creation in space context.
 * Space ID is passed as query parameter to maintain space context.
 * If shouldOpenWriterEditor is true, navigates to writer mode instead of legacy designer.
 */
export const spaceContentCreate = ({
    spaceId,
    type,
    customContentType,
    templateId,
    shouldOpenWriterEditor,
}: {
    spaceId: string;
    type: ContentType;
    customContentType: string;
    templateId?: string;
    shouldOpenWriterEditor?: boolean;
}): Route =>
    shouldOpenWriterEditor
        ? designerContentCreation(customContentType, templateId, spaceId)
        : {
              ...contentCreateRoute,
              params: {
                  ...contentCreateRoute.params,
                  type,
                  customContentType,
                  template: templateId,
              },
              query: {
                  spaceId,
              },
          };

/**
 * Navigate to content editing in space context.
 * Space ID is passed as query parameter to maintain space context.
 * If shouldOpenWriterEditor is true, navigates to writer mode instead of legacy designer.
 */
export const spaceContentEdit = ({
    spaceId,
    contentId,
    shouldOpenWriterEditor,
}: {
    spaceId: string;
    contentId: string;
    shouldOpenWriterEditor?: boolean;
}): Route =>
    shouldOpenWriterEditor
        ? designerContentEdition(contentId, spaceId)
        : {
              ...contentEditRoute,
              params: {
                  ...contentEditRoute.params,
                  key: contentId,
              },
              query: {
                  spaceId,
              },
          };
